import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { Button, ModalHeader, ModalBody, ModalFooter, Modal, Popover, PopoverHeader, PopoverBody, UncontrolledTooltip } from 'reactstrap';
// eslint-disable-next-line no-unused-vars
import StarRatingComponent from 'react-star-rating-component';
import { AudioPreview } from '../../components/FindAudio/AudioPreview';
import ModalConsumer from '../../components/utility/Modal/ModalConsumer';
import LicenseStatusSelector from '../../components/FindAudio/LicenseStatusSelector'; //license status import
import RequestLicenseModal from '../../components/FindAudio/RequestLicenseModal';
import { downloadFile } from '../../helpers/download';
import { usePlaylists } from '../../components/PlaylistContext';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { useOrganizationStore } from '../../store/organization.store';
import { getOrgCollectionPath, AUDIOMETAPATH } from '../../constants/paths';

// Helper to extract a grouping key from a track based on a field name
const getGroupKey = (track, field) => {
  if (!field) return null;

  // Special handling for licenseStatus grouping
  if (field === 'licenseStatus') {
    const ls = track.licenseStatus;
    if (ls && ls.isLicensed) {
      const isBlanket = ls.durationType === 'blanket';
      const hasValidExpiry = ls.expiryDate && ls.expiryDate > Date.now();
      if (isBlanket || hasValidExpiry) return 'Licensed';
    }
    // Also check dateExpires as fallback
    if (track.dateExpires && track.dateExpires > Date.now()) return 'Licensed';
    return 'Unlicensed';
  }

  const val = track[field];
  if (val == null) return 'Uncategorized';
  if (typeof val === 'string') return val || 'Uncategorized';
  if (Array.isArray(val)) return val.map(v => v.label || v).join(', ') || 'Uncategorized';
  if (typeof val === 'object' && val.label) return val.label;
  return String(val) || 'Uncategorized';
};

export { getGroupKey };

// Add AudioErrorBoundary component
class AudioErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('Audio loading error:', error);
    }
  }

  render() {
    if (this.state.hasError) {
      // Return a simple play button as fallback
      return (
        <button 
          className="btn btn-link p-0"
          onClick={() => this.setState({ hasError: false })}
          title="Retry loading audio"
        >
          <i className="fas fa-play"></i>
        </button>
      );
    }

    return this.props.children;
  }
}

const ModalConfirm = (props) => {
  const [modal, setModal] = useState(props.isOpen);
  const toggle = () => {
    setModal(!modal);
  };
  const remove = () => {
    props.onRequestClose();
  };
  const callback = () => {
    props.callback();
    toggle();
  };
  return (
    <div>
      <Modal isOpen={modal} toggle={toggle} onClosed={remove} contentClassName="delete-confirm-modal">
        <ModalHeader 
          close={
            <button
              className="close"
              onClick={toggle}
              style={{
                color: 'var(--accent-1-color)',
                opacity: 1,
                textShadow: 'none',
                background: 'none',
                border: 'none',
                fontSize: '1.75rem',
                fontWeight: 'normal',
                padding: '0.25rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto'
              }}
            >
              ×
            </button>
          }
        >
          {props.title}
        </ModalHeader>
        <ModalBody>{props.body}</ModalBody>
        <ModalFooter>
          <Button 
            className="ml-auto btn-warning-custom" 
            onClick={callback}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Continue</span>
          </Button>
          <Button 
            className="ml-2 btn-primary-custom" 
            onClick={toggle}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Cancel</span>
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

// PublicToggle component for toggling track public/private status
const PublicToggle = ({ track, firebase, updateTrackLicense }) => {
  const [isPublic, setIsPublic] = useState(track.isPublic === true);
  const [isLoading, setIsLoading] = useState(false);
  const currentOrgId = useOrganizationStore((state) => state.currentOrgId);
  const currentOrgName = useOrganizationStore((state) => state.currentOrgName);

  // Check if this is a licensed/shared track (not owned by this org)
  const isLicensedTrack = track.isLicensedTrack === true;

  // Sync with track prop
  useEffect(() => {
    setIsPublic(track.isPublic === true);
  }, [track.isPublic]);

  const handleToggle = async () => {
    // Don't allow toggling for licensed tracks
    if (isLoading || !firebase || !currentOrgId || isLicensedTrack) return;

    setIsLoading(true);
    const newValue = !isPublic;

    try {
      if (newValue) {
        // Publishing to public catalogue
        await firebase.publishToPublicCatalogue(currentOrgId, currentOrgName, track.id, track);
      } else {
        // Unpublishing from public catalogue
        await firebase.unpublishFromPublicCatalogue(currentOrgId, track.id);
      }
      setIsPublic(newValue);

      // Update the parent state so the change persists through re-renders
      if (updateTrackLicense) {
        updateTrackLicense(track.id, { isPublic: newValue });
      }
    } catch (error) {
      console.error('Error toggling public status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Determine button state and styling
  const isDisabled = isLoading || isLicensedTrack;
  const getCursor = () => {
    if (isLicensedTrack) return 'not-allowed';
    if (isLoading) return 'wait';
    return 'pointer';
  };
  const getTitle = () => {
    if (isLicensedTrack) return 'Licensed tracks cannot be made public';
    return isPublic ? 'Click to make private' : 'Click to make public';
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <button
        type="button"
        onClick={handleToggle}
        disabled={isDisabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '20px',
          borderRadius: '10px',
          border: 'none',
          backgroundColor: isLicensedTrack
            ? 'rgba(255, 255, 255, 0.1)'
            : (isPublic ? 'var(--accent-1-color)' : 'rgba(255, 255, 255, 0.2)'),
          cursor: getCursor(),
          transition: 'background-color 0.2s',
          position: 'relative',
          padding: 0,
          opacity: isLicensedTrack ? 0.5 : 1,
        }}
        title={getTitle()}
      >
        <div
          style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: isLicensedTrack ? 'rgba(255, 255, 255, 0.5)' : '#fff',
            position: 'absolute',
            left: isPublic ? '18px' : '2px',
            transition: 'left 0.2s',
            boxShadow: isLicensedTrack ? 'none' : '0 1px 3px rgba(0,0,0,0.3)',
          }}
        />
        {isLoading && (
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
            style={{
              position: 'absolute',
              width: '12px',
              height: '12px',
              color: isPublic ? '#fff' : 'var(--accent-1-color)',
            }}
          />
        )}
      </button>
    </div>
  );
};

const TableRows = ({
  tracks,
  columnsVisible,
  editTrack,
  deleteTrack,
  activeTrack,
  setActiveTrack,
  updateTrackLicense, //license status prop
  updateTrackFields, // generic in-place field update (writes + updates local state)
  activeRowRef,
  selectedPlaylist,
  setNextTrackToAutoplayRef,
  firebase,
  mode, // 'library' (default) or 'public'
  grouped, // true when rendered inside an expanded group
}) => {
  const isPublicMode = mode === 'public';
  // Get org-level role from organization store
  const currentOrgRole = useOrganizationStore((state) => state.currentOrgRole);
  const currentOrgId = useOrganizationStore((state) => state.currentOrgId);
  const currentOrgName = useOrganizationStore((state) => state.currentOrgName);
  const isSuperAdmin = useOrganizationStore((state) => state.isSuperAdmin);
  const role = isSuperAdmin ? 'ADMIN' : (currentOrgRole || 'USER');
  const [popoverOpen, setPopoverOpen] = useState({});
  const [selectedTracks, setSelectedTracks] = useState({});
  const [visibleTracks, setVisibleTracks] = useState(new Set());
  // State for Request License modal (public mode)
  const [requestLicenseModalOpen, setRequestLicenseModalOpen] = useState(false);
  const [selectedTrackForLicense, setSelectedTrackForLicense] = useState(null);
  const ROW_BUFFER_COUNT = 10; // Number of rows to buffer above and below
  const [visibleIndices, setVisibleIndices] = useState(new Set());
  const [rowHeight, setRowHeight] = useState(0);
  const wavePlayerRefs = useRef(new Map());
  const { focusedPlaylists, addTrackToPlaylist, playlistTrackIds, removeTrackFromPlaylist } = usePlaylists();
  const [addingToPlaylists, setAddingToPlaylists] = useState({}); // { [trackId]: true/false }
  // State to store audio durations for visible tracks
  const [audioDurations, setAudioDurations] = useState({});
  // State for tag adder UI
  const [tagAdderOpen, setTagAdderOpen] = useState({}); // { [trackId]: boolean }
  const [tagAdderValue, setTagAdderValue] = useState({}); // { [trackId]: tagObj|null }
  const [availableTags, setAvailableTags] = useState([]);
  // State for tag hover and removal
  const [hoveredTag, setHoveredTag] = useState({}); // { [trackId]: tagValue }
  // const [removingTag, setRemovingTag] = useState({}); // { [trackId]: tagValue }
  // const [pendingRemove, setPendingRemove] = useState(null); // { trackId, tagValue, tagLabel }

  // --- New refs for popover and button ---
  const popoverRefs = useRef({});
  const buttonRefs = useRef({});

  // --- Helper to check if node is a DOM node ---
  const isDOMNode = node => node && typeof node.contains === 'function';

  // --- Effect to close popover on outside click ---
  useEffect(() => {
    const handleClick = (event) => {
      let shouldClose = false;
      Object.keys(popoverOpen).forEach((id) => {
        if (popoverOpen[id]) {
          const popoverNode = popoverRefs.current[id];
          const buttonNode = buttonRefs.current[id];
          if (
            (!isDOMNode(popoverNode) || !popoverNode.contains(event.target)) &&
            (!isDOMNode(buttonNode) || !buttonNode.contains(event.target))
          ) {
            shouldClose = true;
          }
        }
      });
      if (shouldClose) {
        setPopoverOpen((prev) => {
          const newState = { ...prev };
          Object.keys(newState).forEach((id) => {
            newState[id] = false;
          });
          return newState;
        });
      }
    };
    // Only add listener if any popover is open
    if (Object.values(popoverOpen).some(Boolean)) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [popoverOpen]);

  // Cleanup function for wave players
  const cleanupWavePlayer = (trackId) => {
    const wavePlayer = wavePlayerRefs.current.get(trackId);
    if (wavePlayer) {
      // Stop any playing audio
      if (wavePlayer.isPlaying()) {
        wavePlayer.stop();
      }
      // Destroy the wave player instance
      wavePlayer.destroy();
      // Remove from refs
      wavePlayerRefs.current.delete(trackId);
    }
  };

  // Calculate row height when component mounts
  useEffect(() => {
    const calculateRowHeight = () => {
      const firstRow = document.querySelector('[data-track-id]');
      if (firstRow) {
        const height = firstRow.getBoundingClientRect().height;
        setRowHeight(height);
      }
    };

    calculateRowHeight();
    // Recalculate on window resize
    window.addEventListener('resize', calculateRowHeight);
    return () => window.removeEventListener('resize', calculateRowHeight);
  }, []);

  // Add intersection observer to track visible rows
  useEffect(() => {
    if (!rowHeight) return; // Don't set up observer until we have row height

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleIndices(prev => {
          const newSet = new Set(prev);
          let changed = false;
          entries.forEach((entry) => {
            const trackId = entry.target.dataset.trackId;
            const index = tracks.findIndex(t => t.id === trackId);
            if (index === -1) return;
            if (entry.isIntersecting) {
              if (!newSet.has(index)) {
                newSet.add(index);
                changed = true;
              }
            } else {
              if (newSet.has(index)) {
                newSet.delete(index);
                changed = true;
                // Cleanup wave player when track is no longer visible
                cleanupWavePlayer(trackId);
              }
            }
          });
          return changed ? newSet : prev;
        });
      },
      { 
        threshold: 0.1,
        rootMargin: `${ROW_BUFFER_COUNT * rowHeight}px 0px` // Buffer based on row count
      }
    );

    // Observe all track rows
    const trackRows = document.querySelectorAll('[data-track-id]');
    trackRows.forEach(row => observer.observe(row));

    // Capture current refs for cleanup
    const currentRefs = wavePlayerRefs.current;

    return () => {
      trackRows.forEach(row => observer.unobserve(row));
      observer.disconnect();
      // Cleanup all wave players on unmount using captured refs
      currentRefs.forEach((_, trackId) => {
        cleanupWavePlayer(trackId);
      });
    };
  }, [tracks, rowHeight]);

  // Compute the visibleTracks set based on visibleIndices
  useEffect(() => {
    if (visibleIndices.size === 0) {
      setVisibleTracks(new Set());
      return;
    }
    const indices = Array.from(visibleIndices);
    const min = Math.max(0, Math.min(...indices) - ROW_BUFFER_COUNT);
    const max = Math.min(tracks.length - 1, Math.max(...indices) + ROW_BUFFER_COUNT);
    
    setVisibleTracks(prevVisibleTracks => {
      const newVisibleTracks = new Set();
      
      // Cleanup wave players for tracks that are no longer in the buffer
      tracks.forEach(track => {
        if (!newVisibleTracks.has(track.id) && prevVisibleTracks.has(track.id)) {
          cleanupWavePlayer(track.id);
        }
      });

      for (let i = min; i <= max; i++) {
        newVisibleTracks.add(tracks[i].id);
      }
      
      if (activeTrack) {
        const activeTrackIndex = tracks.findIndex(t => t.id === activeTrack);
        if (activeTrackIndex !== -1) {
          const activeMin = Math.max(0, activeTrackIndex - ROW_BUFFER_COUNT);
          const activeMax = Math.min(tracks.length - 1, activeTrackIndex + ROW_BUFFER_COUNT);
          for (let i = activeMin; i <= activeMax; i++) {
            newVisibleTracks.add(tracks[i].id);
          }
        }
      }
      return newVisibleTracks;
    });
  }, [visibleIndices, tracks, activeTrack]);

  // Cleanup on component unmount
  useEffect(() => {
    // Capture current refs for cleanup
    const currentRefs = wavePlayerRefs.current;
    
    return () => {
      // Cleanup all wave players when component unmounts using captured refs
      currentRefs.forEach((_, trackId) => {
        cleanupWavePlayer(trackId);
      });
      currentRefs.clear();
    };
  }, []);

  const togglePopover = (id) => {
    setPopoverOpen(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleTrackChange = (trackId, track) => {
    setSelectedTracks(prev => ({
      ...prev,
      [trackId]: track
    }));
    const activeTrackId = track.storageRef.split('/')[0];
    setActiveTrack(activeTrackId);
    togglePopover(trackId);
    // Clear previous duration so AudioPreview will recalculate for new file
    setAudioDurations(prev => {
      const newDurations = { ...prev };
      delete newDurations[trackId];
      return newDurations;
    });
  };

  const handleDownload = async (event, track) => {
    event.preventDefault();
    if (track.downloadUrl) {
      downloadFile({
        fileName: track.fileName || 'download',
        fileType: 'audio/mpeg', // or 'audio/wav' depending on your file type
        fileUrl: track.downloadUrl,
      });
    } else {
      console.error('Download URL is not available');
    }
  };

  // Request License handlers (for public mode)
  const handleRequestLicense = (track) => {
    setSelectedTrackForLicense(track);
    setRequestLicenseModalOpen(true);
  };

  const handleCloseRequestModal = () => {
    setRequestLicenseModalOpen(false);
    setSelectedTrackForLicense(null);
  };

  const handleSubmitLicenseRequest = async (requestData) => {
    if (!selectedTrackForLicense || !firebase || !currentOrgId) return;

    try {
      await firebase.createLicenseRequest(
        selectedTrackForLicense.sourceOrgId,
        selectedTrackForLicense.sourceTrackId || selectedTrackForLicense.id,
        selectedTrackForLicense.trackName || 'Unknown Track',
        currentOrgId,
        currentOrgName,
        {
          ...requestData,
          trackData: {
            genre: selectedTrackForLicense.genre,
            bpm: selectedTrackForLicense.bpm,
            key: selectedTrackForLicense.key,
            artist: selectedTrackForLicense.artist,
            album: selectedTrackForLicense.album,
          },
        }
      );
      handleCloseRequestModal();
    } catch (error) {
      console.error('Error submitting license request:', error);
      throw error;
    }
  };

  // Use defaultMp3Id for preview if present, else fallback to first mp3 (current logic)
  const getPreviewMp3 = (mp3Files, defaultMp3Id) => {
    if (defaultMp3Id) {
      const found = mp3Files.find(f => f.storageRef === defaultMp3Id);
      if (found) return found;
    }
    return mp3Files[0];
  };

  // Load tags from Firestore once
  useEffect(() => {
    if (!firebase?.db) return;
    const loadTags = async () => {
      const tagsRef = collection(firebase.db, 'tags');
      const tagsSnapshot = await getDocs(tagsRef);
      const tagsData = [];
      tagsSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        tagsData.push({
          id: docSnap.id,
          name: data.label,
          value: data.value,
          label: data.label
        });
      });
      tagsData.sort((a, b) => a.name.localeCompare(b.name));
      setAvailableTags(tagsData);
    };
    loadTags();
  }, [firebase]);

  return [
    ...tracks.map((track) => {
      const {
        // eslint-disable-next-line no-unused-vars
        trackColor,
        trackName,
        composer = [],
        tags = [],
        genre,
        bpm,
        key,
        id,
        // eslint-disable-next-line no-unused-vars
        trackRating,
        jobTitle,
        producer,
        artist,
        album,
        recordLabel,
        publisher,
        agency,
        client,
        creativedirector = [],
        dateCompleted,
        dateExpires,
        flightingPlatform = [],
        flightingCodes = [],
        studio,
        backupLocation,
        sessionArtists = [],
        mp3Files = [],
        wavFiles = [],
        isLicensedTrack,
        sourceOrgName,
        // sourceOrgId available for future use (e.g., audio playback from source org)
      } = track;

      // console.log('Wav', wavFiles);
      // console.log('Mp3', mp3Files);

      const rowStyle = {
        ...(grouped ? { backgroundColor: 'rgba(255, 255, 255, 0.03)' } : {}),
        ...(track.needsAttention ? { backgroundColor: 'rgba(255, 75, 75, 0.1)' } : {}),
        ...(activeTrack === track.id ? { backgroundColor: 'rgba(255, 255, 255, 0.1)' } : {}),
      };

      const tagsList = () => {
        if (tags && tags.length) {
          return (
            <div className="tableTag-container" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0, padding: '2px 5px' }}>
              {tags.map((item) => (
                <ModalConsumer key={uuidv4()}>
                  {({ showModal }) => (
                    <span
                      className="tableTag"
                      style={{
                        display: 'inline-block',
                        margin: 0,
                        padding: '0 5px',
                        height: 15,
                        lineHeight: '15px',
                        position: 'relative',
                        cursor: (['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) ? 'pointer' : 'default',
                        textDecoration: (['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) ? 'line-through' : 'none',
                        textDecorationColor: (['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) ? 'var(--accent-1-color)' : 'inherit',
                        color: (['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) ? 'var(--accent-1-color)' : undefined,
                        transition: 'color 0.15s',
                      }}
                      onMouseEnter={() => ['ADMIN', 'OPERATOR'].includes(role) && setHoveredTag(prev => ({ ...prev, [id]: item.value }))}
                      onMouseLeave={() => ['ADMIN', 'OPERATOR'].includes(role) && setHoveredTag(prev => ({ ...prev, [id]: null }))}
                      title={(['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) ? 'Remove tag' : undefined}
                      onClick={e => {
                        if (['ADMIN', 'OPERATOR'].includes(role) && hoveredTag[id] === item.value) {
                          e.stopPropagation();
                          showModal(ModalConfirm, {
                            isOpen: true,
                            title: 'Remove Tag',
                            body: `Are you sure you want to remove the tag "${item.label}"?`,
                            callback: async () => {
                              const newTags = tags.filter(t => t.value !== item.value);
                              if (updateTrackFields) {
                                await updateTrackFields(id, { tags: newTags });
                              }
                              setHoveredTag(prev => ({ ...prev, [id]: null }));
                            },
                            onRequestClose: () => setHoveredTag(prev => ({ ...prev, [id]: null })),
                          });
                        }
                      }}
                    >
                      {item.label}
                    </span>
                  )}
                </ModalConsumer>
              ))}
              {/* Add tag adder button if tags column is visible and user is Admin/Operator */}
              {columnsVisible.tags && ['ADMIN', 'OPERATOR'].includes(role) && (
                <>
                  <button
                    className="btn btn-link p-0 ml-1"
                    style={{ fontSize: '0.85rem', color: '#6b6bff', lineHeight: 1, padding: 0, width: 18, height: 15, minWidth: 0 }}
                    title="Add tag"
                    onClick={e => {
                      e.stopPropagation();
                      setTagAdderOpen(prev => ({ ...prev, [id]: !prev[id] }));
                    }}
                  >
                    <i className="fas fa-plus-circle" style={{ fontSize: 13, verticalAlign: 'middle' }}></i>
                  </button>
                  {tagAdderOpen[id] && (
                    <div style={{ minWidth: 180, background: 'var(--color-background-secondary)', borderRadius: 6, padding: 8, position: 'absolute', zIndex: 1000 }}>
                      <Autocomplete
                        options={availableTags}
                        getOptionLabel={option => option.label}
                        value={tagAdderValue[id] || null}
                        onChange={async (event, newValue) => {
                          setTagAdderValue(prev => ({ ...prev, [id]: newValue }));
                          setTagAdderOpen(prev => ({ ...prev, [id]: false }));
                          if (!newValue) return;
                          // Merge with existing tags, avoid duplicates
                          const newTags = Array.isArray(tags)
                            ? [...tags.filter(t => t.value !== newValue.value), { label: newValue.label, value: newValue.value }]
                            : [{ label: newValue.label, value: newValue.value }];
                          if (updateTrackFields) {
                            await updateTrackFields(id, { tags: newTags });
                          }
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Add tag"
                            size="small"
                            autoFocus
                            InputProps={{
                              ...params.InputProps,
                              style: {
                                fontSize: 'var(--menu-font-size)',
                                fontFamily: 'var(--font-family-primary)',
                                color: '#fff',
                                background: 'var(--color-background-secondary)'
                              }
                            }}
                            inputProps={{
                              ...params.inputProps,
                              style: {
                                color: '#fff',
                                fontSize: 'var(--menu-font-size)',
                                fontFamily: 'var(--font-family-primary)',
                                background: 'var(--color-background-secondary)'
                              }
                            }}
                          />
                        )}
                        style={{ width: 160, fontSize: 'var(--menu-font-size)', fontFamily: 'var(--font-family-primary)' }}
                        ListboxProps={{ style: { fontSize: 'var(--menu-font-size)', fontFamily: 'var(--font-family-primary)' } }}
                        disableClearable
                        openOnFocus
                        onBlur={() => setTagAdderOpen(prev => ({ ...prev, [id]: false }))}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          );
        }
      };
      const composersList = () => {
        if (composer && composer.length) {
          return composer.map((item) => {
            return (
              <span key={uuidv4()} className="mr-2">
                {item.label}
              </span>
            );
          });
        }
      };

      const producerList = () => {
        if (producer) {
          return (
            <span key={uuidv4()} className="mr-2">
              {producer.label}
            </span>
          );
        }
      };

      const creativeDirectorsList = () => {
        if (creativedirector && creativedirector.length) {
          return creativedirector.map((item) => {
            return (
              <span key={uuidv4()} className="mr-2">
                {item.label}
              </span>
            );
          });
        }
      };

      const flightingPlatformList = () => {
        if (flightingPlatform && flightingPlatform.length) {
          return flightingPlatform.map((item) => {
            return (
              <span key={uuidv4()} className="mr-2">
                {item.label}
              </span>
            );
          });
        }
      };

      const sessionArtistsList = () => {
        if (sessionArtists && sessionArtists.length) {
          return sessionArtists.map((item) => {
            return (
              <span key={uuidv4()} className="mr-2">
                {item.label}
              </span>
            );
          });
        }
      };

      const hasAudio = wavFiles.length > 0 || mp3Files.length > 0;
      const selectedTrack = selectedTracks[id]
        || (mp3Files.length > 0
          ? getPreviewMp3(mp3Files, track.defaultMp3Id)
          : wavFiles[0] || {});
      // Determine if the track is already in any focused playlist
      const isInAnyFocusedPlaylist = focusedPlaylists.some(pid => (playlistTrackIds[pid] || []).includes(id));

      // Move isInSelectedPlaylist logic here, where 'id' is defined
      const isInSelectedPlaylist = selectedPlaylist && (playlistTrackIds[selectedPlaylist] || []).includes(id);

      return (
        <React.Fragment key={id}>
          <tr 
            data-track-id={id}
            style={rowStyle}
            ref={track.id === activeTrack ? activeRowRef : null}
          >
            {columnsVisible.trackName && (
              <td>
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                  <span>{trackName}</span>
                  {isLicensedTrack && (
                    <span
                      id={`licensed-badge-${id}`}
                      style={{
                        fontSize: '0.65rem',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        color: 'var(--accent-1-color)',
                        border: '1px solid var(--accent-1-color)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Licensed
                    </span>
                  )}
                </div>
                {isLicensedTrack && sourceOrgName && (
                  <UncontrolledTooltip target={`licensed-badge-${id}`} placement="top">
                    Licensed from: {sourceOrgName}
                  </UncontrolledTooltip>
                )}
                {columnsVisible.audioFiles && hasAudio && visibleTracks.has(id) && (
                  <AudioErrorBoundary>
                    <AudioPreview
                      mp3Files={mp3Files}
                      wavFiles={wavFiles}
                      id={id}
                      activeTrack={activeTrack}
                      setActiveTrack={setActiveTrack}
                      showButton={false}
                      selectedTrack={selectedTrack}
                      onWavePlayerInit={(wavePlayer) => {
                        wavePlayerRefs.current.set(id, wavePlayer);
                      }}
                      onDuration={(duration) => {
                        setAudioDurations(prev => {
                          // Only update if changed or not set
                          if (prev[id] !== duration) {
                            return { ...prev, [id]: duration };
                          }
                          return prev;
                        });
                      }}
                    />
                  </AudioErrorBoundary>
                )}
                {columnsVisible.tags && tags && tags.length > 0 && (
                  <div className="d-flex flex-wrap mt-2">{tagsList()}</div>
                )}
              </td>
            )}
            <td className="text-right">
              {/* 1. Add to focused playlists button */}
              <span id={`add-playlist-btn-wrapper-${id}`}> 
                <Button
                  id={`add-playlist-btn-${id}`}
                  className="btn-icon btn-round mb-0 ml-1"
                  size="sm"
                  style={{ 
                    background: 'transparent',
                    color: isInAnyFocusedPlaylist ? 'var(--accent-1-color)' : '#ffffff',
                    opacity: !isInAnyFocusedPlaylist && focusedPlaylists.length === 0 ? 0.5 : 1,
                    transition: 'none'
                  }}
                  disabled={focusedPlaylists.length === 0 || addingToPlaylists[id] || isInAnyFocusedPlaylist || track.needsAttention === true}
                  title={track.needsAttention ? 'Cannot add flagged track to playlist' : undefined}
                  onClick={async () => {
                    if (track.needsAttention) return;
                    setAddingToPlaylists(prev => ({ ...prev, [id]: true }));
                    try {
                      await Promise.all(
                        focusedPlaylists.map(pid => addTrackToPlaylist(pid, id))
                      );
                    } finally {
                      setTimeout(() => setAddingToPlaylists(prev => ({ ...prev, [id]: false })), 800);
                    }
                  }}
                >
                  <span className="d-flex flex-row align-items-center">
                    {isInAnyFocusedPlaylist ? (
                      <i className="fas fa-check-circle"></i>
                    ) : (
                    <i className="fas fa-plus-circle"></i>
                    )}
                  </span>
                </Button>
              </span>
              <UncontrolledTooltip 
                key={
                  selectedPlaylist && isInSelectedPlaylist
                    ? 'already-in-playlist'
                    : focusedPlaylists.length === 0
                      ? 'no-playlist'
                      : 'other'
                }
                placement="top" 
                target={`add-playlist-btn-wrapper-${id}`}
                autohide={true}
                delay={{ show: 2000, hide: 0 }}
                trigger={selectedPlaylist && isInSelectedPlaylist ? 'hover' : (focusedPlaylists.length === 0 ? 'hover click' : 'hover')}
                container="body"
                fade={false}
              >
                {selectedPlaylist && isInSelectedPlaylist
                  ? 'Already in playlist'
                  : (isInAnyFocusedPlaylist
                      ? 'Already in playlist'
                      : (focusedPlaylists.length > 0
                          ? 'Add to checked playlists'
                          : 'Select checkboxes on playlists to add tracks'))}
              </UncontrolledTooltip>
              {/* 2. Remove from playlist button (if visible) */}
              {selectedPlaylist && isInSelectedPlaylist && (
                <>
                  <Button
                    id={`remove-playlist-btn-${id}`}
                    className="btn-icon btn-round mb-0 ml-1 remove-from-playlist-btn"
                    size="sm"
                    style={{
                      background: 'transparent',
                      color: '#ffffff',
                      transition: 'color 0.2s'
                    }}
                    onClick={async () => {
                      // Only trigger next-track autoplay if the removed track is the currently active track
                      if (activeTrack === id) {
                        const currentIndex = tracks.findIndex(t => t.id === id);
                        let nextTrackId = null;
                        if (currentIndex !== -1) {
                          // Prefer next track, otherwise previous if at end
                          if (currentIndex < tracks.length - 1) {
                            nextTrackId = tracks[currentIndex + 1].id;
                          } else if (currentIndex > 0) {
                            nextTrackId = tracks[currentIndex - 1].id;
                          }
                          if (nextTrackId && nextTrackId !== id && setNextTrackToAutoplayRef) {
                            setNextTrackToAutoplayRef(nextTrackId);
                          }
                        }
                      }
                      await removeTrackFromPlaylist(selectedPlaylist, id);
                    }}
                  >
                    <span className="d-flex flex-row align-items-center">
                      <i className="fas fa-minus-circle"></i>
                    </span>
                  </Button>
                  <UncontrolledTooltip placement="top" target={`remove-playlist-btn-${id}`} autohide={true} delay={{ show: 2000, hide: 0 }} trigger="hover" container="body" fade={false}>Remove from selected playlist</UncontrolledTooltip>
                </>
              )}
              {/* 3. Select audio tracks button */}
              {hasAudio && (
                <>
                  <Button
                    id={`audio-btn-${id}`}
                    className="btn-icon btn-round mb-0 ml-1"
                    size="sm"
                    style={{ 
                      background: 'transparent',
                      color: '#ffffff',
                      transition: 'none'
                    }}
                    onClick={() => togglePopover(id)}
                    ref={el => (buttonRefs.current[id] = el)}
                  >
                    <span className="d-flex flex-row align-items-center">
                      <i className="fas fa-list"></i>
                    </span>
                  </Button>
                  <UncontrolledTooltip placement="top" target={`audio-btn-${id}`} autohide={true} delay={{ show: 2000, hide: 0 }} trigger="hover" container="body" fade={false}>Audio files & download</UncontrolledTooltip>
                  <Popover
                    placement="bottom"
                    isOpen={popoverOpen[id]}
                    target={`audio-btn-${id}`}
                    toggle={() => togglePopover(id)}
                    innerRef={el => (popoverRefs.current[id] = el)}
                    container="body"
                    fade={false}
                  >
                    <PopoverHeader>Select Audio File</PopoverHeader>
                    <PopoverBody>
                      {[...mp3Files, ...wavFiles]
                        .slice() // copy array
                        .sort((a, b) => (a.fileName || '').localeCompare(b.fileName || ''))
                        .map((audioFile, index) => {
                          const isFlagged = track.needsAttention === true;
                          return (
                          <button
                            key={`${audioFile.storageRef}-${index}`}
                            className={`popover-action ${selectedTrack.storageRef === audioFile.storageRef ? 'selected' : ''}`}
                            onClick={() => handleTrackChange(id, audioFile)}
                          >
                            <span>{audioFile.fileName}</span>
                            <button
                              className="download-btn"
                              disabled={isFlagged}
                              title={isFlagged ? 'Download disabled — track is flagged' : 'Download'}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: '0',
                                marginLeft: '8px',
                                color: 'inherit',
                                opacity: isFlagged ? 0.3 : 1,
                                cursor: isFlagged ? 'not-allowed' : 'pointer',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isFlagged) return;
                                handleDownload(e, audioFile);
                              }}
                            >
                              <i className="fas fa-download"></i>
                            </button>
                          </button>
                          );
                        })}
                    </PopoverBody>
                  </Popover>
                </>
              )}
              {/* 4. Edit track button - hidden in public mode */}
              {!isPublicMode && ['ADMIN', 'OPERATOR'].includes(role) && (
                <>
                  <Button
                    id={`edit-btn-${id}`}
                    className="btn-icon btn-round mb-0 ml-1"
                    size="sm"
                    style={{
                      background: 'transparent',
                      color: '#ffffff',
                      transition: 'none'
                    }}
                    onClick={() => {
                      editTrack(id);
                    }}
                  >
                    <span className="d-flex flex-row align-items-center">
                      <i className="tim-icons icon-pencil"></i>
                    </span>
                  </Button>
                  <UncontrolledTooltip placement="top" target={`edit-btn-${id}`} autohide={true} delay={{ show: 2000, hide: 0 }} trigger="hover" container="body" fade={false}>Edit track</UncontrolledTooltip>
                </>
              )}
              {/* 5. Delete track button - hidden in public mode */}
              {!isPublicMode && role === 'ADMIN' && (
                <ModalConsumer>
                  {({ showModal }) => (
                    <>
                      <Button
                        id={`delete-btn-${id}`}
                        className="btn-icon btn-round"
                        size="sm"
                        style={{
                          background: 'transparent',
                          color: '#ffffff',
                          transition: 'none'
                        }}
                        onClick={() => {
                          showModal(ModalConfirm, {
                            isOpen: true,
                            title: 'Delete Track',
                            body: `Are you sure you want to delete ${trackName}?`,
                            callback: () => {
                              deleteTrack(id);
                            },
                          });
                        }}
                      >
                        <span className="d-flex flex-row align-items-center">
                          <i className="tim-icons icon-trash-simple"></i>
                        </span>
                      </Button>
                      <UncontrolledTooltip placement="top" target={`delete-btn-${id}`} autohide={true} delay={{ show: 2000, hide: 0 }} trigger="hover" container="body" fade={false}>Delete track</UncontrolledTooltip>
                    </>
                  )}
                </ModalConsumer>
              )}
            </td>
            {columnsVisible.length && (
              <td style={{ minWidth: 60, maxWidth: 70, width: 70, textAlign: 'center' }}>
                {audioDurations[id] !== undefined
                  ? moment.utc(audioDurations[id] * 1000).format('mm:ss')
                  : '--'}
              </td>
            )}
            {/* License Status - hidden in public mode */}
            {!isPublicMode && columnsVisible.licenseStatus && (
              <td style={{ textAlign: 'center' }}>
                <LicenseStatusSelector
                  track={track}
                  updateTrackLicense={updateTrackLicense}
                  firebase={firebase}
                />
              </td>
            )}
            {/* Public Toggle - hidden in public mode */}
            {!isPublicMode && columnsVisible.isPublic && (
              <td style={{ textAlign: 'center' }}>
                <PublicToggle
                  track={track}
                  firebase={firebase}
                  updateTrackLicense={updateTrackLicense}
                />
              </td>
            )}
            {/* Request License button - shown only in public mode */}
            {isPublicMode && (
              <td style={{ textAlign: 'center' }}>
                <Button
                  size="sm"
                  className="btn-primary-custom"
                  onClick={() => handleRequestLicense(track)}
                  disabled={track.sourceOrgId === currentOrgId}
                  title={track.sourceOrgId === currentOrgId ? 'This is your track' : 'Request a license for this track'}
                  style={{
                    padding: '4px 12px',
                    fontSize: '12px',
                  }}
                >
                  {track.sourceOrgId === currentOrgId ? 'Your Track' : 'Request'}
                </Button>
              </td>
            )}
            {/* Organization - shown only in public mode */}
            {isPublicMode && (
              <td>
                <small>{track.sourceOrgName || '-'}</small>
              </td>
            )}
            {columnsVisible.composers && <td>{composersList()}</td>}
            {columnsVisible.genre && <td>{genre && genre.label}</td>}
            {columnsVisible.bpm && <td>{bpm && bpm.label}</td>}
            {columnsVisible.key && <td>{key && key.label}</td>}
            {columnsVisible.jobTitle && <td>{jobTitle}</td>}
            {columnsVisible.producer && <td>{producerList()}</td>}
            {columnsVisible.artist && <td>{artist && artist.label}</td>}
            {columnsVisible.album && <td>{album && album.label}</td>}
            {columnsVisible.recordLabel && <td>{recordLabel && recordLabel.label}</td>}
            {columnsVisible.publisher && <td>{publisher && publisher.label}</td>}
            {columnsVisible.agency && <td>{agency && agency.label}</td>}
            {columnsVisible.client && <td>{client && client.label}</td>}
            {columnsVisible.creativeDirector && (
              <td>{creativeDirectorsList()}</td>
            )}
            {columnsVisible.dateCompleted && (
              <td>
                {(dateCompleted &&
                  moment(dateCompleted).format('DD-MM-YYYY')) ||
                  '--'}
              </td>
            )}
            {columnsVisible.dateExpires && (
              <td>
                {(dateExpires && moment(dateExpires).format('DD-MM-YYYY')) ||
                  '--'}
              </td>
            )}
            {columnsVisible.flightingPlatform && (
              <td>{flightingPlatformList()}</td>
            )}
            {columnsVisible.flightingCode && (
              <td>{flightingCodes?.length > 0 && flightingCodes[0]}</td>
            )}
            {columnsVisible.studio && <td>{studio && studio.label}</td>}
            {columnsVisible.backupLocation && (
              <td>{backupLocation && backupLocation.label}</td>
            )}
            {columnsVisible.sessionArtists && <td>{sessionArtistsList()}</td>}
            {columnsVisible.trackRating && <td></td>}
            {columnsVisible.flag && (
              <td style={{ textAlign: 'left', width: '60px', paddingLeft: '12px' }}>
                {['ADMIN', 'OPERATOR'].includes(role) && (
                  <>
                    <Button
                      id={`flag-btn-${id}`}
                      className="btn-icon mb-0 flag-button"
                      color="link"
                      size="sm"
                      style={{ 
                        background: 'transparent',
                        color: track.needsAttention ? '#ff4b4b' : 'rgba(255, 255, 255, 0.5)',
                        border: 'none',
                        transition: 'color 0.2s ease',
                        padding: '0.375rem 0.75rem'
                      }}
                      onClick={async () => {

                        try {
                          if (!firebase || !firebase.db) {
                            console.error('Firebase or firebase.db is not available');
                            return;
                          }

                          const orgId = useOrganizationStore.getState().currentOrgId;
                          if (!orgId) {
                            console.error('No organization selected');
                            return;
                          }
                          const trackRef = doc(firebase.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), id);
                          const newValue = !track.needsAttention;

                          // Optimistically update local state for immediate UI feedback
                          track.needsAttention = newValue;
                          const btn = document.getElementById(`flag-btn-${id}`);
                          if (btn) btn.style.color = newValue ? '#ff4b4b' : 'rgba(255, 255, 255, 0.5)';

                          await updateDoc(trackRef, {
                            needsAttention: newValue
                          });

                          if (newValue) {
                            // Flagging: remove from public library
                            if (track.isPublic && firebase.unpublishFromPublicCatalogue) {
                              await firebase.unpublishFromPublicCatalogue(orgId, id);
                            }
                          } else {
                            // Unflagging: republish only if no active exclusive license
                            if (!track.isPublic && firebase.publishToPublicCatalogue) {
                              const activeExclusive = await firebase.getActiveExclusiveLicense(orgId, id);
                              if (!activeExclusive) {
                                const currentOrgName = useOrganizationStore.getState().currentOrgName;
                                await firebase.publishToPublicCatalogue(orgId, currentOrgName, id, track);
                              }
                            }
                          }

                        } catch (error) {
                          console.error('Error updating flag:', error);
                          console.error('Error details:', error.message, error.code);
                        }
                      }}
                    >
                      <span className="d-flex flex-row align-items-center">
                        <i className="fas fa-flag"></i>
                      </span>
                    </Button>
                    <UncontrolledTooltip placement="top" target={`flag-btn-${id}`} autohide={true} delay={{ show: 2000, hide: 0 }} trigger="hover" container="body" fade={false}>
                      Flagged tracks are not visible to basic users
                    </UncontrolledTooltip>
                  </>
                )}
              </td>
            )}
          </tr>
        </React.Fragment>
      );
    }),
    // Request License Modal (for public mode)
    isPublicMode && requestLicenseModalOpen && selectedTrackForLicense && (
      <RequestLicenseModal
        key="request-license-modal"
        isOpen={requestLicenseModalOpen}
        onClose={handleCloseRequestModal}
        onSubmit={handleSubmitLicenseRequest}
        trackName={selectedTrackForLicense.trackName || 'Unknown Track'}
        sourceOrgName={selectedTrackForLicense.sourceOrgName}
        firebase={firebase}
      />
    ),
  ].filter(Boolean);
};

export default TableRows;

<style>{`
.remove-from-playlist-btn:hover {
  color: var(--accent-1-color) !important;
}

button.btn.btn-link.btn-sm.btn-icon.flag-button:hover,
button.btn.btn-link.btn-sm.btn-icon.flag-button:focus,
button.btn.btn-link.btn-sm.btn-icon.flag-button:active,
.btn.btn-link.btn-sm.btn-icon.flag-button:hover,
.btn.btn-link.btn-sm.btn-icon.flag-button:focus,
.btn.btn-link.btn-sm.btn-icon.flag-button:active {
  color: #ffffff !important;
  background: transparent !important;
  background-color: transparent !important;
  border-color: transparent !important;
  box-shadow: none !important;
  transform: none !important;
  text-decoration: none !important;
}
`}</style>
